import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import projectTemplateMasterService from '../../services/projectTemplateMasterService';
import RevisionModal from '../../components/RevisionModal/RevisionModal';
import '../../components/RevisionModal/RevisionModal.css';
import { toast } from 'react-toastify';

const ProjectTemplateMasterEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('basicInfo');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [formData, setFormData] = useState({
    templateCode: '',
    templateName: '',
    templateDescription: '',
    templateCategory: 'Web Development',
    templateType: 'Standard',
    templateStatus: 'Active',
    supportedProjectTypes: [],
    supportedTechnologies: [],
    templateStructure: {
      phases: [],
      milestones: [],
      tasks: []
    },
    resourceRequirements: {
      requiredRoles: [],
      teamSize: { min: 1, max: 5 },
      estimatedTotalHours: 0
    },
    qualityStandards: {
      codeReviewRequired: true,
      testingRequired: true,
      documentationRequired: true,
      deploymentApprovalRequired: true,
      qualityGates: []
    },
    configurationSettings: {
      environmentSetup: [],
      deploymentSettings: {
        deploymentType: 'Manual',
        deploymentSteps: [],
        rollbackPlan: ''
      },
      monitoringSettings: {
        monitoringRequired: true,
        metrics: [],
        alertingRules: []
      }
    },
    templateMetadata: {
      version: '1.0.0',
      usageCount: 0,
      successRate: 0,
      averageCompletionTime: 0,
      tags: [],
      complexity: 'Medium'
    }
  });

  const categoryOptions = [
    'Web Development',
    'Mobile Development',
    'Desktop Application',
    'API Development',
    'Data Science',
    'AI/ML',
    'DevOps',
    'Cloud Migration',
    'Legacy Modernization',
    'Support',
    'Maintenance'
  ];

  const typeOptions = [
    'Standard',
    'Custom',
    'Framework',
    'Library',
    'Integration',
    'Migration'
  ];

  const statusOptions = [
    'Active',
    'Inactive',
    'Deprecated',
    'Under Review'
  ];

  const projectTypeOptions = [
    'Internal',
    'External',
    'Support',
    'Development'
  ];

  const complexityOptions = [
    'Low',
    'Medium',
    'High',
    'Very High'
  ];

  const deploymentTypeOptions = [
    'Manual',
    'Automated',
    'Semi-automated'
  ];

  useEffect(() => {
    if (location.state?.projectTemplateData) {
      setFormData(location.state.projectTemplateData);
      setInitialLoading(false);
    } else {
      fetchProjectTemplateMaster();
    }
  }, [id, location.state]);

  const fetchProjectTemplateMaster = async () => {
    try {
      setInitialLoading(true);
      const response = await projectTemplateMasterService.getProjectTemplateMasterById(id);
      
      if (response.success) {
        setFormData(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch project template master');
        navigate('/projects/project-template-master');
      }
    } catch (error) {
      console.error('Error fetching project template master:', error);
      toast.error('Failed to fetch project template master');
      navigate('/projects/project-template-master');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addArrayItem = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem || '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    if (!formData.templateCode.trim()) {
      toast.error('Template Code is required');
      return false;
    }
    if (!/^[A-Z0-9-]+$/.test(formData.templateCode.trim())) {
      toast.error('Template Code must contain only uppercase letters, numbers, and hyphens');
      return false;
    }
    if (!formData.templateName.trim()) {
      toast.error('Template Name is required');
      return false;
    }
    if (!formData.templateDescription.trim()) {
      toast.error('Template Description is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setPendingFormData(formData);
    setShowRevisionModal(true);
  };

  const handleRevisionSave = async (revisionData) => {
    try {
      setLoading(true);
      const response = await projectTemplateMasterService.updateProjectTemplateMaster(id, {
        ...pendingFormData,
        changeReason: revisionData.reason
      });

      if (response.success) {
        toast.success('Project Template Master updated successfully!');
        navigate('/projects/project-template-master');
      } else {
        toast.error(response.message || 'Failed to update Project Template Master');
      }
    } catch (error) {
      console.error('Error updating Project Template Master:', error);
      toast.error('Failed to update Project Template Master');
    } finally {
      setLoading(false);
      setShowRevisionModal(false);
      setPendingFormData(null);
    }
  };

  const handleBack = () => {
    navigate('/projects/project-template-master');
  };

  if (initialLoading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button
            className="btn btn-outline-secondary me-3"
            onClick={handleBack}
          >
            <ArrowLeft size={20} className="me-2" />
            Back
          </button>
          <h1 className="h3 mb-0 d-inline">Edit Project Template Master</h1>
        </div>
        <button
          type="submit"
          form="projectTemplateForm"
          className="btn btn-primary"
          disabled={loading}
        >
          <Save size={20} className="me-2" />
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <form id="projectTemplateForm" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Tabs */}
        <div className="card shadow mb-4">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'basicInfo' ? 'active' : ''}`}
                  onClick={() => setActiveTab('basicInfo')}
                >
                  <span className="me-2">📋</span>
                  Basic Information
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'structure' ? 'active' : ''}`}
                  onClick={() => setActiveTab('structure')}
                >
                  <span className="me-2">🏗️</span>
                  Template Structure
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'resources' ? 'active' : ''}`}
                  onClick={() => setActiveTab('resources')}
                >
                  <span className="me-2">👥</span>
                  Resource Requirements
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'quality' ? 'active' : ''}`}
                  onClick={() => setActiveTab('quality')}
                >
                  <span className="me-2">✅</span>
                  Quality & Standards
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'configuration' ? 'active' : ''}`}
                  onClick={() => setActiveTab('configuration')}
                >
                  <span className="me-2">⚙️</span>
                  Configuration
                </button>
              </li>
            </ul>
          </div>

          <div className="card-body">
            {/* Basic Information Tab */}
            {activeTab === 'basicInfo' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Template Code *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.templateCode}
                      onChange={(e) => handleInputChange('templateCode', e.target.value.toUpperCase())}
                      placeholder="e.g., WEB-REACT"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Template Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.templateName}
                      onChange={(e) => handleInputChange('templateName', e.target.value)}
                      placeholder="e.g., React Web Application"
                      required
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.templateDescription}
                      onChange={(e) => handleInputChange('templateDescription', e.target.value)}
                      placeholder="Describe the template purpose, features, and use cases..."
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Category *</label>
                    <select
                      className="form-select"
                      value={formData.templateCategory}
                      onChange={(e) => handleInputChange('templateCategory', e.target.value)}
                      required
                    >
                      {categoryOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Type *</label>
                    <select
                      className="form-select"
                      value={formData.templateType}
                      onChange={(e) => handleInputChange('templateType', e.target.value)}
                      required
                    >
                      {typeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.templateStatus}
                      onChange={(e) => handleInputChange('templateStatus', e.target.value)}
                    >
                      {statusOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Template Version</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.templateMetadata.version}
                      onChange={(e) => handleNestedInputChange('templateMetadata', 'version', e.target.value)}
                      placeholder="e.g., 1.0.0"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Template Complexity</label>
                    <select
                      className="form-select"
                      value={formData.templateMetadata.complexity}
                      onChange={(e) => handleNestedInputChange('templateMetadata', 'complexity', e.target.value)}
                    >
                      {complexityOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Supported Project Types</label>
                    <div className="row">
                      {projectTypeOptions.map(option => (
                        <div key={option} className="col-md-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={formData.supportedProjectTypes.includes(option)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  addArrayItem('supportedProjectTypes', option);
                                } else {
                                  removeArrayItem('supportedProjectTypes', formData.supportedProjectTypes.indexOf(option));
                                }
                              }}
                            />
                            <label className="form-check-label">{option}</label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Template Structure Tab */}
            {activeTab === 'structure' && (
              <div className="row">
                <div className="col-12">
                  <div className="mb-4">
                    <h6>Template Phases</h6>
                    <div className="d-flex gap-2 mb-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => addArrayItem('templateStructure.phases', { 
                          phaseName: '', 
                          phaseDescription: '', 
                          estimatedDuration: 1, 
                          deliverables: [], 
                          dependencies: [], 
                          isOptional: false 
                        })}
                      >
                        <Plus size={16} />
                        Add Phase
                      </button>
                    </div>
                    <div className="list-group">
                      {formData.templateStructure.phases.map((phase, index) => (
                        <div key={index} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="row">
                                <div className="col-md-6">
                                  <input
                                    type="text"
                                    className="form-control mb-2"
                                    placeholder="Phase name"
                                    value={phase.phaseName}
                                    onChange={(e) => {
                                      const newPhases = [...formData.templateStructure.phases];
                                      newPhases[index] = { ...newPhases[index], phaseName: e.target.value };
                                      handleInputChange('templateStructure', { ...formData.templateStructure, phases: newPhases });
                                    }}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <input
                                    type="number"
                                    className="form-control mb-2"
                                    placeholder="Duration (days)"
                                    value={phase.estimatedDuration}
                                    onChange={(e) => {
                                      const newPhases = [...formData.templateStructure.phases];
                                      newPhases[index] = { ...newPhases[index], estimatedDuration: parseInt(e.target.value) || 1 };
                                      handleInputChange('templateStructure', { ...formData.templateStructure, phases: newPhases });
                                    }}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => {
                                      const newPhases = formData.templateStructure.phases.filter((_, i) => i !== index);
                                      handleInputChange('templateStructure', { ...formData.templateStructure, phases: newPhases });
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Resource Requirements Tab */}
            {activeTab === 'resources' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Minimum Team Size</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      value={formData.resourceRequirements.teamSize.min}
                      onChange={(e) => handleNestedInputChange('resourceRequirements.teamSize', 'min', parseInt(e.target.value) || 1)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Maximum Team Size</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      value={formData.resourceRequirements.teamSize.max}
                      onChange={(e) => handleNestedInputChange('resourceRequirements.teamSize', 'max', parseInt(e.target.value) || 5)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Estimated Total Hours</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.resourceRequirements.estimatedTotalHours}
                      onChange={(e) => handleNestedInputChange('resourceRequirements', 'estimatedTotalHours', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Quality & Standards Tab */}
            {activeTab === 'quality' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.qualityStandards.codeReviewRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'codeReviewRequired', e.target.checked)}
                    />
                    <label className="form-check-label">Code Review Required</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.qualityStandards.testingRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'testingRequired', e.target.checked)}
                    />
                    <label className="form-check-label">Testing Required</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.qualityStandards.documentationRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'documentationRequired', e.target.checked)}
                    />
                    <label className="form-check-label">Documentation Required</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.qualityStandards.deploymentApprovalRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'deploymentApprovalRequired', e.target.checked)}
                    />
                    <label className="form-check-label">Deployment Approval Required</label>
                  </div>
                </div>
              </div>
            )}

            {/* Configuration Tab */}
            {activeTab === 'configuration' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Deployment Type</label>
                    <select
                      className="form-select"
                      value={formData.configurationSettings.deploymentSettings.deploymentType}
                      onChange={(e) => handleNestedInputChange('configurationSettings.deploymentSettings', 'deploymentType', e.target.value)}
                    >
                      {deploymentTypeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.configurationSettings.monitoringSettings.monitoringRequired}
                      onChange={(e) => handleNestedInputChange('configurationSettings.monitoringSettings', 'monitoringRequired', e.target.checked)}
                    />
                    <label className="form-check-label">Monitoring Required</label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Revision Modal */}
      <RevisionModal
        isOpen={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        onSave={handleRevisionSave}
        currentRevision={formData.revisionHistory?.length + 1 || 1}
      />
    </div>
  );
};

export default ProjectTemplateMasterEdit;
